/**
 * Supplier Portal Dashboard - Main Entry Point
 * Scope 3 Command Center for Value Chain Data Collection
 * 
 * Connected to backend APIs:
 * - GET /api/v1/supplier-portal/dashboard
 * - GET /api/v1/supplier-portal/surveys
 */
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Globe, 
  ClipboardList, 
  Building2, 
  Plus, 
  ChevronRight,
  Send,
  CheckCircle,
  Clock,
  TrendingUp,
  Link2,
  Users,
  ShieldCheck,
  ArrowRight,
  Eye,
  AlertCircle,
  RefreshCw,
  Mail,
  BarChart3,
  FileText
} from 'lucide-react';
import { apiFetch } from '../../../services/api_service';

export default function SupplierPortalDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [surveys, setSurveys] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [dashboardData, surveysData] = await Promise.all([
        apiFetch('/supplier-portal/dashboard'),
        apiFetch('/supplier-portal/surveys?limit=5')
      ]);

      if (dashboardData?._error) throw new Error(dashboardData.message || 'Failed to fetch dashboard');
      if (surveysData?._error) throw new Error(surveysData.message || 'Failed to fetch surveys');

      setStats(dashboardData.stats);
      setRecentActivity(dashboardData.recentActivity || []);
      setSurveys(surveysData.surveys || []);
    } catch (err) {
      console.error('Failed to load dashboard:', err);
      setError(err.message);
      // Set default stats for error state
      setStats({
        activeSurveys: 0,
        totalSuppliers: 0,
        responsesThisMonth: 0,
        avgResponseRate: 0
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]" data-testid="supplier-portal-loading">
        <div className="flex flex-col items-center gap-3">
          <RefreshCw size={32} className="text-purple-400 animate-spin" />
          <span className="text-[#6B6B6B] text-sm">Loading Supplier Portal...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="supplier-portal-dashboard">
      {/* Hero Header */}
      <div className="relative overflow-hidden bg-gradient-to-br from-purple-900/50 via-purple-800/30 to-indigo-900/50 border border-purple-500/30 rounded-2xl p-8">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMtOS45NDEgMC0xOCA4LjA1OS0xOCAxOHM4LjA1OSAxOCAxOCAxOCAxOC04LjA1OSAxOC0xOC04LjA1OS0xOC0xOC0xOHptMCAzMmMtNy43MzIgMC0xNC02LjI2OC0xNC0xNHM2LjI2OC0xNCAxNC0xNCAxNCA2LjI2OCAxNCAxNC02LjI2OCAxNC0xNCAxNHoiIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iLjAyIi8+PC9nPjwvc3ZnPg==')] opacity-30" />
        
        <div className="relative flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2 text-purple-300 text-sm mb-3">
              <Link2 size={14} />
              <span>Value Chain</span>
              <ChevronRight size={14} />
              <span className="text-white font-medium">Supplier Portal</span>
            </div>
            
            <h1 className="text-3xl font-bold text-white mb-2 flex items-center gap-3">
              <Globe className="text-purple-400" size={32} />
              Supplier Data Collection
              <span className="px-3 py-1 rounded-full bg-purple-500/30 border border-purple-400/50 text-sm font-medium text-purple-200">
                Scope 3 Command Center
              </span>
            </h1>
            
            <p className="text-purple-200/80 text-base max-w-2xl">
              Streamline value chain data collection for CSRD, CDP, GRI, and TCFD compliance. 
              One survey satisfies multiple frameworks simultaneously.
            </p>
          </div>

          <button 
            onClick={() => navigate('/value-chain/supplier-portal/surveys/new')}
            className="px-5 py-2.5 rounded-lg bg-white text-purple-700 font-semibold hover:bg-purple-50 transition-colors flex items-center gap-2 shadow-lg shadow-purple-500/20"
            data-testid="create-survey-btn"
          >
            <Plus size={18} />
            Create Survey
          </button>
        </div>

        {/* Stats Grid */}
        {stats && (
          <div className="grid md:grid-cols-4 gap-4 mt-8">
            <StatsCard 
              label="Active Surveys" 
              value={stats.activeSurveys} 
              subtext="Currently open"
              color="purple"
            />
            <StatsCard 
              label="Total Suppliers" 
              value={stats.totalSuppliers} 
              subtext="In database"
              color="blue"
            />
            <StatsCard 
              label="Responses This Month" 
              value={stats.responsesThisMonth} 
              subtext="New submissions"
              color="cyan"
            />
            <StatsCard 
              label="Avg Response Rate" 
              value={`${stats.avgResponseRate}%`} 
              subtext="Across all surveys"
              color="emerald"
              highlight
            />
          </div>
        )}
      </div>

      {/* Error Banner */}
      {error && (
        <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-xl flex items-center gap-3">
          <AlertCircle className="text-amber-400 shrink-0" size={20} />
          <div>
            <p className="text-amber-400 font-medium text-sm">Connection Issue</p>
            <p className="text-amber-400/70 text-xs">{error}. Showing cached data.</p>
          </div>
          <button 
            onClick={fetchDashboardData}
            className="ml-auto px-3 py-1.5 bg-amber-500/20 text-amber-400 rounded-lg text-sm flex items-center gap-1 hover:bg-amber-500/30"
          >
            <RefreshCw size={14} />
            Retry
          </button>
        </div>
      )}

      {/* Main Content */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Left Column - Surveys */}
        <div className="lg:col-span-2 space-y-6">
          {/* Recent Surveys */}
          <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl">
            <div className="flex items-center justify-between p-5 border-b border-[#1F1F1F]">
              <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                <ClipboardList className="text-purple-400" size={20} />
                Recent Surveys
              </h2>
              <Link 
                to="/value-chain/supplier-portal/surveys" 
                className="text-sm text-purple-400 hover:text-purple-300 flex items-center gap-1"
              >
                View All <ArrowRight size={14} />
              </Link>
            </div>

            <div className="divide-y divide-[#1F1F1F]">
              {surveys.length === 0 ? (
                <EmptyState 
                  icon={ClipboardList}
                  title="No surveys yet"
                  description="Create your first supplier data collection survey to start gathering Scope 3 emissions data."
                  actionLabel="Create First Survey"
                  onAction={() => navigate('/value-chain/supplier-portal/surveys/new')}
                />
              ) : (
                surveys.map((survey) => (
                  <SurveyListItem key={survey.id} survey={survey} />
                ))
              )}
            </div>
          </div>

          {/* Three Superpowers Banner */}
          <div className="bg-gradient-to-br from-teal-900/30 to-cyan-900/30 rounded-xl border border-teal-500/30 p-6">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <TrendingUp className="text-teal-400" size={20} />
              Three Superpowers in Action
            </h3>

            <div className="grid md:grid-cols-3 gap-4">
              <SuperpowerCard 
                icon={Link2}
                number="1"
                title="Cross-Framework Intelligence"
                description="One survey satisfies CSRD, CDP, GRI, and TCFD. No duplicate data requests."
              />
              <SuperpowerCard 
                icon={Users}
                number="2"
                title="Ownership & Accountability"
                description="Track which supplier provided data. Approval chain ensures quality."
              />
              <SuperpowerCard 
                icon={ShieldCheck}
                number="3"
                title="Audit Defensibility"
                description="Every action logged. Complete audit trail from request to disclosure."
              />
            </div>
          </div>
        </div>

        {/* Right Column - Quick Actions & Activity */}
        <div className="space-y-6">
          {/* Quick Actions */}
          <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-5">
            <h3 className="text-base font-semibold text-white mb-4 flex items-center gap-2">
              <TrendingUp className="text-purple-400" size={18} />
              Quick Actions
            </h3>

            <div className="space-y-2">
              <QuickActionButton 
                icon={ClipboardList}
                label="Create Survey"
                to="/value-chain/supplier-portal/surveys/new"
              />
              <QuickActionButton 
                icon={Building2}
                label="Manage Suppliers"
                to="/value-chain/supplier-portal/suppliers"
              />
              <QuickActionButton 
                icon={BarChart3}
                label="View All Responses"
                to="/value-chain/supplier-portal/surveys"
              />
              <QuickActionButton 
                icon={ShieldCheck}
                label="Audit Trail"
                to="/audit/trail"
              />
            </div>
          </div>

          {/* Recent Activity */}
          <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-5">
            <h3 className="text-base font-semibold text-white mb-4 flex items-center gap-2">
              <Clock className="text-purple-400" size={18} />
              Recent Activity
            </h3>

            {recentActivity.length === 0 ? (
              <p className="text-sm text-[#6B6B6B] text-center py-4">
                No recent activity
              </p>
            ) : (
              <div className="space-y-3">
                {recentActivity.slice(0, 5).map((activity, idx) => (
                  <ActivityItem key={idx} activity={activity} />
                ))}
              </div>
            )}
          </div>

          {/* Flywheel Integration */}
          <FlywheelIntegrationCard />
        </div>
      </div>
    </div>
  );
}

// Sub-components
function StatsCard({ label, value, subtext, color, highlight }) {
  const colorClasses = {
    purple: 'bg-purple-500/10 border-purple-500/30',
    blue: 'bg-blue-500/10 border-blue-500/30',
    cyan: 'bg-cyan-500/10 border-cyan-500/30',
    emerald: highlight ? 'bg-emerald-500/20 border-emerald-400/50' : 'bg-emerald-500/10 border-emerald-500/30'
  };

  return (
    <div className={`p-4 rounded-xl border ${colorClasses[color]}`}>
      <p className="text-xs text-white/70 mb-1">{label}</p>
      <p className={`text-3xl font-bold ${highlight ? 'text-emerald-300' : 'text-white'}`}>
        {value}
      </p>
      <p className="text-xs text-white/50 mt-1">{subtext}</p>
    </div>
  );
}

function SurveyListItem({ survey }) {
  const responseRate = survey.progress?.totalRecipients > 0
    ? Math.round((survey.progress?.surveysSubmitted / survey.progress?.totalRecipients) * 100)
    : 0;

  const isActive = !survey.timeline?.closedAt && survey.timeline?.launchedAt;
  const isDraft = !survey.timeline?.launchedAt;

  return (
    <Link 
      to={`/value-chain/supplier-portal/surveys/${survey.id}`}
      className="block p-4 hover:bg-[#1F1F1F]/50 transition-colors"
      data-testid={`survey-item-${survey.id}`}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="px-2 py-0.5 rounded bg-[#1F1F1F] text-[10px] font-mono text-[#A0A0A0]">
              {survey.surveyId}
            </span>
            {isDraft ? (
              <span className="px-2 py-0.5 rounded bg-amber-500/20 text-amber-400 text-[10px] font-medium">
                Draft
              </span>
            ) : isActive ? (
              <span className="px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-400 text-[10px] font-medium flex items-center gap-1">
                <span className="w-1 h-1 bg-emerald-400 rounded-full animate-pulse" />
                Active
              </span>
            ) : (
              <span className="px-2 py-0.5 rounded bg-[#2F2F2F] text-[#6B6B6B] text-[10px] font-medium">
                Closed
              </span>
            )}
          </div>

          <h4 className="text-sm font-medium text-white truncate">{survey.surveyName}</h4>
          
          <div className="flex items-center gap-3 mt-2 text-xs text-[#6B6B6B]">
            <span className="flex items-center gap-1">
              <Send size={10} /> {survey.progress?.emailsSent || 0} sent
            </span>
            <span className="flex items-center gap-1">
              <CheckCircle size={10} /> {survey.progress?.surveysSubmitted || 0} responses
            </span>
          </div>
        </div>

        <div className="text-center shrink-0">
          <div className={`w-12 h-12 rounded-full border-2 flex items-center justify-center ${
            responseRate >= 80 ? 'border-emerald-500 text-emerald-400' :
            responseRate >= 50 ? 'border-amber-500 text-amber-400' :
            'border-red-500 text-red-400'
          }`}>
            <span className="text-sm font-bold">{responseRate}%</span>
          </div>
        </div>
      </div>
    </Link>
  );
}

function SuperpowerCard({ icon: Icon, number, title, description }) {
  return (
    <div className="p-4 bg-[#0A0A0A]/50 rounded-lg border border-[#1F1F1F]">
      <div className="flex items-center gap-2 mb-2">
        <Icon className="text-teal-400" size={18} />
        <span className="text-xs text-teal-400 font-bold">#{number}</span>
      </div>
      <h4 className="text-sm font-semibold text-white mb-1">{title}</h4>
      <p className="text-xs text-[#6B6B6B]">{description}</p>
    </div>
  );
}

function QuickActionButton({ icon: Icon, label, to }) {
  return (
    <Link to={to}>
      <button className="w-full text-left px-4 py-3 rounded-lg border border-[#1F1F1F] hover:bg-[#1F1F1F] hover:border-purple-500/30 transition-all flex items-center justify-between group">
        <span className="flex items-center gap-3">
          <Icon className="text-[#6B6B6B] group-hover:text-purple-400 transition-colors" size={18} />
          <span className="text-sm font-medium text-white">{label}</span>
        </span>
        <ChevronRight className="text-[#3F3F3F] group-hover:text-purple-400 transition-colors" size={16} />
      </button>
    </Link>
  );
}

function ActivityItem({ activity }) {
  return (
    <div className="flex items-start gap-3 p-2 rounded-lg hover:bg-[#1F1F1F]/50">
      <div className="w-8 h-8 rounded-full bg-purple-500/20 flex items-center justify-center shrink-0">
        <Mail className="text-purple-400" size={14} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm text-white truncate">{activity.supplierName || 'Unknown'}</p>
        <p className="text-xs text-[#6B6B6B]">
          {activity.status === 'submitted' ? 'Submitted response' : activity.status}
        </p>
      </div>
      <span className="text-[10px] text-[#6B6B6B] shrink-0">
        {activity.submittedAt ? new Date(activity.submittedAt).toLocaleDateString() : ''}
      </span>
    </div>
  );
}

function FlywheelIntegrationCard() {
  return (
    <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-5">
      <h3 className="text-base font-semibold text-white mb-3 flex items-center gap-2">
        <ArrowRight className="text-purple-400" size={16} />
        Defensibility Flywheel
      </h3>
      <p className="text-xs text-[#6B6B6B] mb-4">
        Supplier data flows seamlessly through your compliance system:
      </p>

      <div className="space-y-2 text-xs">
        {[
          'Framework requires Scope 3 data',
          'Creates Obligation',
          'Supplier Portal collects data',
          'Auto-creates Evidence',
          'Updates Claims',
          'Feeds into Disclosures',
          'Complete Audit Trail'
        ].map((step, idx) => (
          <div key={idx} className={`flex items-center gap-2 ${idx === 2 ? 'font-semibold text-white' : 'text-[#A0A0A0]'}`}>
            <span className="text-purple-400">{idx + 1}.</span>
            <span>{step}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function EmptyState({ icon: Icon, title, description, actionLabel, onAction }) {
  return (
    <div className="p-12 text-center">
      <Icon className="mx-auto text-[#2F2F2F] mb-4" size={48} />
      <h3 className="text-lg font-semibold text-white mb-2">{title}</h3>
      <p className="text-sm text-[#6B6B6B] mb-6 max-w-sm mx-auto">{description}</p>
      <button 
        onClick={onAction}
        className="px-5 py-2.5 rounded-lg bg-purple-500/20 border border-purple-500/30 text-purple-400 font-medium hover:bg-purple-500/30 transition-colors"
      >
        {actionLabel}
      </button>
    </div>
  );
}
